import { emitDevHarness } from "@/dev-harness/telemetry";
import type { RuntimeBus } from "@/runtime/bus";
import type {
  InstallFile,
  InstallRecord,
  Lane,
  LaneManifest,
  RuntimeEvent,
} from "@/runtime/events";
import {
  installFileInWorker,
  installFileSegmentInWorker,
} from "@/runtime/heavy-operation-client";
import {
  clearCheckpointsForFile,
  clearCheckpointsForLane,
  getCheckpoint,
  listCheckpointsForFile,
  makeCheckpoint,
  saveCheckpoint,
} from "@/storage/install-checkpoints";
import { removeOpfsFile } from "@/storage/opfs";
import { getOpfsPathForFilePath } from "@/install/install-flow";

const SEGMENT_THRESHOLD_BYTES = 4 * 1024 * 1024;

export interface OrchestratorOptions {
  bus: RuntimeBus;
}

interface FilePlan {
  file: InstallFile;
  segmented: boolean;
  totalBytes: number;
  segmentCount: number;
}

function planFiles(manifest: LaneManifest): FilePlan[] {
  return manifest.files.map((file) => {
    const segmented = Array.isArray(file.segments) && file.segments.length > 0;
    const totalBytes = segmented
      ? file.segments!.reduce((sum, segment) => sum + segment.bytes, 0)
      : file.bytes;
    return {
      file,
      segmented,
      totalBytes,
      segmentCount: segmented ? file.segments!.length : 1,
    };
  });
}

/**
 * Install one lane manifest into OPFS. The orchestrator runs on the main thread
 * and posts one worker task per file (small files) or per segment (large
 * segmented files). Between segments of a large file, the worker is destroyed and
 * respawned so no single worker process owns more than ~4 MiB of fetch+hash+write
 * activity. Per-segment SHA-256 verification + IndexedDB checkpoints make the
 * install resumable across tab eviction and page reload.
 */
export async function orchestrateLaneInstall(
  manifest: LaneManifest,
  options: OrchestratorOptions,
): Promise<InstallRecord> {
  const { bus } = options;
  const plan = planFiles(manifest);
  const startedAt = performance.now();

  emitDevHarness({
    kind: "summary",
    event: "install.orchestrate.start",
    data: {
      lane: manifest.lane,
      manifest_id: manifest.id,
      file_count: plan.length,
      segmented_files: plan.filter((entry) => entry.segmented).length,
      total_bytes: plan.reduce((sum, entry) => sum + entry.totalBytes, 0),
      segment_threshold: SEGMENT_THRESHOLD_BYTES,
    },
  });

  let totalReceived = 0;
  const totalBytes = plan.reduce((sum, entry) => sum + entry.totalBytes, 0);

  for (const entry of plan) {
    if (entry.segmented) {
      totalReceived += await installSegmentedFile(
        manifest.lane,
        manifest.id,
        entry,
        bus,
        totalReceived,
        totalBytes,
      );
    } else {
      totalReceived += await installWholeFile(
        manifest.lane,
        manifest.id,
        entry.file,
        bus,
        totalReceived,
        totalBytes,
      );
    }
  }

  // Successful lane install: clear all checkpoints for this manifest so a future
  // re-install starts fresh.
  await clearCheckpointsForLane(manifest.lane, manifest.id);

  await new Promise((resolve) => globalThis.setTimeout(resolve, 180));
  bus.publish({ type: "install.ready", lane: manifest.lane, manifestId: manifest.id });

  emitDevHarness({
    kind: "summary",
    event: "install.orchestrate.ok",
    data: {
      lane: manifest.lane,
      manifest_id: manifest.id,
      total_bytes: totalReceived,
      elapsed_ms: Math.round(performance.now() - startedAt),
    },
  });

  return {
    id: manifest.id,
    lane: manifest.lane,
    version: manifest.version,
    bytes: manifest.bytes,
    installedAt: new Date().toISOString(),
    warmedAt: null,
    status: "ready",
  };
}

async function installWholeFile(
  lane: Lane,
  manifestId: string,
  file: InstallFile,
  bus: RuntimeBus,
  baselineReceived: number,
  totalBytes: number,
): Promise<number> {
  const result = await installFileInWorker(lane, manifestId, file, {
    onRuntimeEvent: (event) => bus.publish(event),
  });

  // Whole-file install emits a single segment-style progress so the UI advances
  // off zero on every file boundary.
  bus.publish({
    type: "install.progress",
    lane,
    received: baselineReceived + result.bytes,
    total: totalBytes,
  });

  return result.bytes;
}

async function installSegmentedFile(
  lane: Lane,
  manifestId: string,
  entry: FilePlan,
  bus: RuntimeBus,
  baselineReceived: number,
  totalBytes: number,
): Promise<number> {
  const file = entry.file;
  const segments = file.segments!;
  let received = 0;

  // Walk existing checkpoints to figure out where to resume. Each checkpoint
  // means that segment was verified to disk in a prior session.
  const existing = await listCheckpointsForFile(lane, manifestId, file.path);
  const completedIndices = new Set<number>();
  for (const checkpoint of existing) {
    completedIndices.add(checkpoint.segmentIndex);
  }

  if (completedIndices.size > 0 && completedIndices.size < segments.length) {
    emitDevHarness({
      kind: "summary",
      event: "install.segmented_file.resume",
      data: {
        lane,
        manifest_id: manifestId,
        file: file.path,
        completed_segments: completedIndices.size,
        total_segments: segments.length,
      },
    });
  }

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];

    if (completedIndices.has(index)) {
      received += segment.bytes;
      bus.publish({
        type: "install.progress",
        lane,
        received: baselineReceived + received,
        total: totalBytes,
      });
      continue;
    }

    const resetFile = index === 0 && completedIndices.size === 0;
    const segmentStartedAt = performance.now();
    // First/last flags drive the expensive OPFS ground-truth walk inside
    // the segment installer. "First" here means "first not-yet-installed
    // segment index on a genuinely fresh file", so a resumed install does
    // not re-run the pre-file walk. Last is simply index === len - 1.
    const isFirstSegment = index === 0 && completedIndices.size === 0;
    const isLastSegment = index === segments.length - 1;

    let result;
    try {
      result = await installFileSegmentInWorker(
        lane,
        manifestId,
        file.path,
        segment,
        index,
        resetFile,
        isFirstSegment,
        isLastSegment,
        { onRuntimeEvent: (event) => bus.publish(event) },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "segment install failed";
      emitDevHarness({
        level: "error",
        kind: "incident",
        event: "install.segmented_file.segment_failed",
        msg: message,
        data: {
          lane,
          manifest_id: manifestId,
          file: file.path,
          segment_index: index,
          offset: segment.offset,
          bytes: segment.bytes,
          elapsed_ms: Math.round(performance.now() - segmentStartedAt),
        },
      });

      // Per the consultant brief: if a segment hash fails, delete the partial
      // OPFS file and restart that file from segment zero. The /^Segment.*checksum
      // mismatch/ test catches the verification path; range/206 errors are
      // re-thrown without OPFS deletion (they may be transient network issues).
      if (/checksum mismatch/i.test(message)) {
        await clearCheckpointsForFile(lane, manifestId, file.path);
        await removeOpfsFile(getOpfsPathForFilePath(lane, manifestId, file.path)).catch(
          () => undefined,
        );
        emitDevHarness({
          level: "warning",
          kind: "incident",
          event: "install.segmented_file.reset",
          data: {
            lane,
            manifest_id: manifestId,
            file: file.path,
            reason: "segment-checksum-mismatch",
          },
        });
      }

      throw error;
    }

    await saveCheckpoint(
      makeCheckpoint(
        lane,
        manifestId,
        file.path,
        result.segmentIndex,
        result.offset,
        result.bytes,
        segment.sha256,
      ),
    );

    received += result.bytes;
    bus.publish({
      type: "install.progress",
      lane,
      received: baselineReceived + received,
      total: totalBytes,
    });

    emitDevHarness({
      kind: "summary",
      event: "install.segment_checkpoint",
      data: {
        lane,
        manifest_id: manifestId,
        file: file.path,
        segment_index: result.segmentIndex,
        offset: result.offset,
        bytes: result.bytes,
        chunks_seen: result.chunksSeen,
        elapsed_ms: result.durationMs,
        completed_segments: index + 1,
        total_segments: segments.length,
      },
    });

    // We deliberately do NOT terminate the worker between segments of a file.
    //
    // The consultant's original memory-defense tactic was to spawn + terminate
    // a worker per segment so no single worker owned more than 4 MiB of work.
    // That design held a real memory bound inside each worker but generated
    // heavy spawn/terminate churn — 99 respawns for a 415 MB GGUF, ~15 for
    // Moonshine. In iPhone Safari Home Screen testing on 2026-04-24 the
    // churn itself appears to push the WebContent process over the eviction
    // threshold: iOS is slow to reclaim terminated-worker heap pages, so
    // successive spawns accumulate non-GC'd allocations faster than the OS
    // frees them. A fresh STT smoke on worker_generation ~15 hit abrupt-kill
    // at `runtime.smoke.start` with carryover_queue_count=29.
    //
    // The within-worker memory bound is preserved by a different mechanism:
    // each segment task opens a fresh sync access handle, writes the bytes,
    // closes the handle, and returns. install-flow.ts retains no cross-segment
    // state inside the worker, so a single worker handling N segments of the
    // same file sees the same peak memory as handling one.
    //
    // Termination still happens on `post-smoke-release` (when the user has
    // loaded an inference runtime and needs it cleared) and when a task fails.
    // See worker.terminate events with reason="post-smoke-release" vs
    // reason="large-install-segment-release" — the latter should no longer
    // appear. Bench 04 / 05 continue to validate end-to-end install flow.
  }

  // Every segment of this file is done. Clear that file's checkpoints — the lane
  // orchestrator will re-clear at the end too, but per-file cleanup keeps the
  // checkpoint store small if a later file fails.
  await clearCheckpointsForFile(lane, manifestId, file.path);

  emitDevHarness({
    kind: "summary",
    event: "install.segmented_file.done",
    data: {
      lane,
      manifest_id: manifestId,
      file: file.path,
      bytes: received,
      segment_count: segments.length,
    },
  });

  return received;
}
